define([
  'es6!sumo/util',
  'js-cookie',
  'immutable',
  'underscore',
  'es6!launcher/util/constants',
  'es6!launcher/actions/Actions',
  'es6!launcher/actions/SettingsActions',
], (Util, jscookie, Immutable, _, Constants, Actions, SettingsActions) => {
  const initialState = Immutable.Map({
    // hasLoaded is set when we load settings. We need it so we don't do certain
    // things without having the correct settings. For instance, if the user is pro
    // but the settings load after the popup, the popup will fail validation and the
    // default popup will be shown.
    hasLoaded: false,
    siteId: null,
    siteUrl: '',
    siteName: '',
    sitePlatform: null,
    cartId: null,
    isPro: false,
    isAdmin: false,
    sumoUrl: null,
    launcherApps: [],
    showUpsellModal: false,
    promoOptOuts: null,
    hasPromoOptOutCookie: jscookie.get('__smPromo'),
    promo: null,
    payments: {},
    shouldAlertHeaderClose: null,
    activeApp: null,
    defaultApp: null,
    linkedApp: null,
    notificationCounts: Immutable.Map(),
    shouldPromptShopifyReview: false,
    appFormData: {},
    accountSettings: Immutable.Map(),
    accountUsers: Immutable.List(),
    seatsIncluded: null,
    hasSeatsRemaining: false,
    trialingInventory: {},
    isDoubleOptInActive: false,
    emailSettings: Immutable.Map(),
  });

  const actionHandlers = {};

  actionHandlers[Actions.RECEIVE_SETTINGS] = (state, action) => {
    const settings = action.settings;

    return state.merge(Immutable.fromJS(_.extend(settings, { hasLoaded: true })));
  };

  // Format subscription trial data for determining limits and status
  actionHandlers[Actions.RECEIVE_TRIALING_INVENTORY] = (state, action) => {
    let { trialingInventory } = action;
    const hasTrialEndingNoticeCookie = jscookie.get('__smTrialEndingNotice');

    if (hasTrialEndingNoticeCookie) trialingInventory = Object.assign(trialingInventory, { shouldShowTrialEndingNotice: false });

    return state.setIn(['trialingInventory'], trialingInventory);
  };

  actionHandlers[Actions.RECEIVE_APPS] = (state, action) => {
    const { apps, defaultApp, notificationCounts } = action.data;
    const TITLE_APPS = [
      'c9a64ad6-0863-4b66-bc0c-ef0fb5ef15d0', // Store
      'ee27a0af-9947-40c9-8eab-0ab6a4f7a9c1', // Notifications
      '5d26e2da-aef5-41c1-947a-624497281723', // Settings
      'c3da7147-2536-4768-af12-50317edc18ae', // Shortcuts
    ];

    let launcherApps = _.reject(apps, (app) => TITLE_APPS.indexOf(app.app.id) !== -1);
    launcherApps = Immutable.fromJS(launcherApps.map((app) => {
      if (app.app.id === Constants.SHARE_APP_UUID) {
        app.app.id = 'share';
      }

      if (app.app.id === Constants.DASHBOARD_APP_UUID) {
        app.app.id = 'dashboard';
      }

      if (app.app.id === Constants.IMAGE_SHARER_APP_UUID) {
        app.app.id = 'image-sharer';
      }

      if (app.app.id === Constants.LIST_BUILDER_3_APP_UUID) {
        app.app.id = 'listbuilder';
      }

      return app;
    }));

    const hasProApps = !!launcherApps.find((app) => app.get('pro'));
    const showUpsellModal = action.data.showUpsell && !hasProApps;

    return state.merge(Immutable.Map({
      launcherApps,
      cartId: action.data.cartId,
      showUpsellModal,
      shouldAutoCreateForm: action.data.shouldAutoCreateForm,
      promo: state.get('promo') || action.data.promo,
      payments: action.data.payments,
      activatedAt: action.data.activatedAt,
      defaultApp,
      notificationCounts: Immutable.Map(notificationCounts),
    }));
  };

  actionHandlers[Actions.UPDATE_ENABLE_APP] = (state, action) => {
    const launcherApps = state.get('launcherApps');

    const activeApp = launcherApps.findEntry((app) => app.get('app').get('id') === action.appId);
    const newStatus = 'installed';

    return state.setIn(['launcherApps', activeApp[0]], activeApp[1].set('status', newStatus));
  };

  actionHandlers[Actions.UPDATE_DISABLE_APP] = (state, action) => {
    const launcherApps = state.get('launcherApps');

    const activeApp = launcherApps.findEntry((app) => app.get('app').get('id') === action.appId);
    const newStatus = 'deleted';

    return state.setIn(['launcherApps', activeApp[0]], activeApp[1].set('status', newStatus));
  };

  actionHandlers[Actions.UPDATE_SETTING] = (state, action) => {
    let newState = state;
    const settingName = action.setting;
    const settingValue = action.value;

    if (settingName === 'activeApp' && settingValue === 'launcher') {
      newState = newState.setIn(['activeApp'], null);
    } else if (Array.isArray(settingName)) {
      newState = newState.setIn(settingName, settingValue);
    } else {
      newState = newState.setIn([settingName], settingValue);
    }

    return newState;
  };

  actionHandlers[SettingsActions.RECEIVE_ACCOUNT] = (state, action) => {
    return state.merge(Immutable.Map({
      accountSettings: Immutable.fromJS(action.data),
    }));
  };

  actionHandlers[SettingsActions.RECEIVE_USERS] = (state, action) => {
    return state.merge(Immutable.Map({
      accountUsers: Immutable.fromJS(action.data.users),
      seatsIncluded: Immutable.fromJS(action.data.seatsIncluded),
      hasSeatsRemaining: Immutable.fromJS(action.data.hasSeatsRemaining),
    }));
  };

  actionHandlers[SettingsActions.RECEIVE_NEW_PASSWORD] = (state, action) => {
    let newState = state;
    newState = newState.set('currentPasswordError', action.currentPasswordError);
    newState = newState.set('newPasswordError', action.newPasswordError);
    newState = newState.set('confirmPasswordError', action.confirmPasswordError);
    return newState;
  };

  actionHandlers[Actions.RECEIVE_SWITCH_PREVIEW] = (state, action) => {
    const switchPreview = action.switchPreview;
    return state.set('switchPreview', switchPreview ? Immutable.fromJS(switchPreview) : null)
  };

  actionHandlers[SettingsActions.RECEIVE_EMAIL_SETTINGS] = (state, action) => {
    return state.merge(Immutable.fromJS({
      emailSettings: action.data,
    }));
  };

  return Util.createReducer(initialState, actionHandlers);
});
